import { getErrorMessage } from '../../../constants/common-error-messages'
import { COP_API_BASE_URL } from '../../../constants/service-endpoints'
import { get } from '../../../services/http.service'
import { ToastService } from '../../../services/toast.service'

const ASSET_DETAILS_ERROR_ACTION = 'asset-details-loading' as const

const ASSET_DETAILS_FUNCTION_MAP: Record<number, string> = {
  1: 'GetMicroSCADAAssetDetails',
  2: 'GetIEDAssetDetails',
  3: 'GetRTUAssetDetails',
  4: 'GetWirelessAssetDetails',
  6: 'GetGPQSSAssetDetails',
  13: 'GetHVAssetDetails',
  14: 'GetHVAssetDetails',
  15: 'GetHVAssetDetails',
  16: 'GetHVAssetDetails',
  17: 'GetHVAssetDetails',
  18: 'GetHVAssetDetails',
  19: 'GetTransformerAssetDetails',
  20: 'GetTransformerAssetDetails',
  21: 'GetTransformerAssetDetails',
  22: 'GetTransformerAssetDetails',
  23: 'GetESSAAssetDetails',
  24: 'GetTransformerAssetDetails',
}

export interface MicroSCADAAssetDto {
  '@odata.context'?: string
  id: string
  name: string
  customerId: string
  contentId: string
  parentAssetId: string | null
  siteArea: string | null
  siteId: string
  siteName: string
  productCategoryId: string
  productName: string
  serialNumber: string
  status: string
  latitude: number
  longitude: number
  lifecycleStatus: string
  operatingSystem: string
  productRevision: string
  productVersion: string
  decom: boolean
  spare: boolean
}

function getCategoryIdFromUrl(): number | null {
  const param = new URLSearchParams(window.location.search).get('product-category-id')
  if (!param) return null
  const id = Number.parseInt(param, 10)
  return Number.isNaN(id) ? null : id
}

function getAssetDetailsUrlByCategory(assetId: string, categoryId: number): string {
  const functionName = ASSET_DETAILS_FUNCTION_MAP[categoryId]
  if (!functionName) {
    throw new Error(`Unknown product category ID: ${categoryId}`)
  }
  const escaped = assetId.replace(/'/g, "''")
  return `/api/v2/odata/assets/${functionName}(assetId='${escaped}')`
}

export async function fetchAssetDetails(assetId: string): Promise<MicroSCADAAssetDto | null> {
  try {
    const id = assetId || getAssetIdFromUrl()
    if (!id) {
      const fallbackMessage = getErrorMessage(ASSET_DETAILS_ERROR_ACTION)
      ToastService.error(fallbackMessage)
      return null
    }

    const categoryId = getCategoryIdFromUrl()
    if (!categoryId) {
      const fallbackMessage = getErrorMessage(ASSET_DETAILS_ERROR_ACTION)
      ToastService.error(fallbackMessage)
      return null
    }

    const endpoint = `${COP_API_BASE_URL}${getAssetDetailsUrlByCategory(id, categoryId)}`
    const result = await get<MicroSCADAAssetDto>(endpoint)
    if (!result || Object.keys(result).length === 0) {
      return null
    }
    return result
  } catch (error) {
    const fallbackMessage = getErrorMessage(ASSET_DETAILS_ERROR_ACTION)
    const message = error instanceof Error && error.message ? error.message : fallbackMessage
    ToastService.error(message)
    return null
  }
}

export function getAssetIdFromUrl(): string | null {
  // Explicit query params take precedence.
  const urlParams = new URLSearchParams(window.location.search)
  const fromQuery = urlParams.get('id') ?? urlParams.get('assetId')
  if (fromQuery) return fromQuery

  const segments = window.location.pathname.split('/').filter(Boolean)
  const last = segments.at(-1)
  if (!last || last.endsWith('.html') || last === 'ied') return null
  return last
}
